import { GOVERNORATE_LABELS } from '../scholarshipApplication/constants';
import {
  cleanPhone,
  extractErrorMessage,
  normalizeArabicDigits,
} from '../scholarshipApplication/validation';
import { EGYPT_CITIES } from './egyptCities';
import {
  OTHER_CITY_OPTION,
  type AcceptanceFieldErrors,
  type AcceptanceFieldKey,
  type AcceptanceFormValues,
  type BackupPhoneRelation,
  type ScholarshipAcceptancePayload,
  type ScholarshipAcceptanceResponse,
} from './types';

export { normalizeArabicDigits, cleanPhone, extractErrorMessage };

export const FLOOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'house', label: 'بيت مستقل / فيلا' },
  { value: 'ground', label: 'الدور الأرضي' },
  ...Array.from({ length: 30 }, (_, idx) => {
    const n = String(idx + 1);
    return { value: n, label: `الدور ${n}` };
  }),
];

export const FLOOR_LABELS: Record<string, string> = Object.fromEntries(
  FLOOR_OPTIONS.map((opt) => [opt.value, opt.label])
);

export const BACKUP_PHONE_RELATION_OPTIONS: Array<{
  value: BackupPhoneRelation;
  label: string;
}> = [
  { value: 'self', label: 'رقم تاني ليّا' },
  { value: 'father', label: 'الأب' },
  { value: 'mother', label: 'الأم' },
  { value: 'sibling', label: 'أخ / أخت' },
  { value: 'relative', label: 'قريب' },
  { value: 'other', label: 'حد تاني' },
];

export const BACKUP_PHONE_RELATION_LABELS: Record<BackupPhoneRelation, string> =
  Object.fromEntries(
    BACKUP_PHONE_RELATION_OPTIONS.map((opt) => [opt.value, opt.label])
  ) as Record<BackupPhoneRelation, string>;

const VALID_FLOORS = new Set(FLOOR_OPTIONS.map((opt) => opt.value));
const VALID_RELATIONS = new Set<string>(
  BACKUP_PHONE_RELATION_OPTIONS.map((opt) => opt.value)
);

export const ORDERED_ACCEPTANCE_FIELDS: AcceptanceFieldKey[] = [
  'social_share_consent',
  'recipient_name',
  'governorate',
  'city',
  'area',
  'street',
  'building',
  'floor',
  'apartment',
  'landmark',
  'postal_code',
  'phone',
  'backup_phone',
  'backup_phone_relation',
];

export function normalizeEgyptianPhone(raw: string): string {
  let cleaned = cleanPhone(raw.trim());
  if (/^001[0125]\d{8}$/.test(cleaned)) {
    cleaned = cleaned.slice(1);
  } else if (/^201[0125]\d{8}$/.test(cleaned)) {
    cleaned = '0' + cleaned.slice(2);
  } else if (/^1[0125]\d{8}$/.test(cleaned)) {
    cleaned = '0' + cleaned;
  }
  return cleaned;
}

export function validatePhone(phone: string): boolean {
  return /^01[0125]\d{8}$/.test(normalizeEgyptianPhone(phone));
}

export function hydrateCity(
  governorate: string | null,
  city: string | null
): { cityChoice: string; customCity: string } {
  const trimmedCity = city?.trim() ?? '';
  if (!trimmedCity || !governorate) {
    return { cityChoice: '', customCity: '' };
  }
  const govCities = EGYPT_CITIES[governorate] ?? [];
  if (govCities.includes(trimmedCity)) {
    return { cityChoice: trimmedCity, customCity: '' };
  }
  return { cityChoice: OTHER_CITY_OPTION, customCity: trimmedCity };
}

export function hydrateAcceptanceForm(
  data: ScholarshipAcceptanceResponse
): AcceptanceFormValues {
  const { cityChoice, customCity } = hydrateCity(data.governorate, data.city);
  return {
    social_share_consent:
      typeof data.social_share_consent === 'boolean'
        ? data.social_share_consent
        : null,
    recipient_name: data.recipient_name ?? data.full_name ?? '',
    governorate: data.governorate ?? '',
    cityChoice,
    customCity,
    area: data.area ?? '',
    street: data.street ?? '',
    building: data.building ?? '',
    floor: data.floor ?? '',
    apartment: data.floor === 'house' ? '' : (data.apartment ?? ''),
    landmark: data.landmark ?? '',
    postal_code: data.postal_code ?? '',
    phone: data.phone ?? '',
    backup_phone: data.backup_phone ?? '',
    backup_phone_relation: data.backup_phone_relation ?? '',
  };
}

export function validateAcceptanceField(
  field: AcceptanceFieldKey,
  values: AcceptanceFormValues
): string | undefined {
  switch (field) {
    case 'social_share_consent': {
      if (typeof values.social_share_consent !== 'boolean') {
        return 'اختار أيوه أو لا';
      }
      return undefined;
    }

    case 'recipient_name': {
      const trimmed = values.recipient_name.trim();
      if (!trimmed) return 'اكتب اسم المستلم بالكامل';
      if (trimmed.length < 3 || trimmed.length > 120) {
        return 'الاسم لازم يكون من 3 لـ 120 حرف';
      }
      if (!trimmed.includes(' ') || trimmed.split(/\s+/).filter(Boolean).length < 2) {
        return 'اكتب اسم المستلم بالكامل';
      }
      return undefined;
    }

    case 'governorate': {
      const trimmed = values.governorate.trim();
      if (!trimmed) return 'اختار المحافظة';
      if (!GOVERNORATE_LABELS[trimmed]) return 'اختار محافظة من القايمة';
      return undefined;
    }

    case 'city': {
      if (!values.cityChoice) return 'اختار المدينة أو المركز';
      const resolved = (
        values.cityChoice === OTHER_CITY_OPTION
          ? values.customCity
          : values.cityChoice
      ).trim();
      if (!resolved) return 'اكتب اسم المدينة أو المركز';
      if (resolved.length < 2 || resolved.length > 80) {
        return 'اسم المدينة لازم يكون من حرفين لـ 80 حرف';
      }
      return undefined;
    }

    case 'area': {
      const trimmed = values.area.trim();
      if (!trimmed) return 'اكتب المنطقة أو الحي أو القرية';
      if (trimmed.length < 2 || trimmed.length > 120) {
        return 'المنطقة لازم تكون من حرفين لـ 120 حرف';
      }
      return undefined;
    }

    case 'street': {
      const trimmed = values.street.trim();
      if (!trimmed) return 'اكتب اسم الشارع';
      if (trimmed.length < 2 || trimmed.length > 150) {
        return 'اسم الشارع لازم يكون من حرفين لـ 150 حرف';
      }
      return undefined;
    }

    case 'building': {
      const trimmed = normalizeArabicDigits(values.building).trim();
      if (!trimmed) return 'اكتب رقم العمارة أو المنزل';
      if (trimmed.length < 1 || trimmed.length > 20) {
        return 'رقم العمارة لازم مايزيدش عن 20 حرف';
      }
      return undefined;
    }

    case 'floor': {
      const trimmed = values.floor.trim();
      if (!trimmed) return 'اختار الدور';
      if (!VALID_FLOORS.has(trimmed)) return 'اختار الدور من القايمة';
      return undefined;
    }

    case 'apartment': {
      if (values.floor === 'house') return undefined;
      const trimmed = normalizeArabicDigits(values.apartment).trim();
      if (trimmed.length > 20) {
        return 'رقم الشقة لازم مايزيدش عن 20 حرف';
      }
      return undefined;
    }

    case 'landmark': {
      const trimmed = values.landmark.trim();
      if (!trimmed) return 'اكتب أقرب علامة مميزة';
      if (trimmed.length < 3 || trimmed.length > 200) {
        return 'العلامة المميزة لازم تكون من 3 لـ 200 حرف';
      }
      return undefined;
    }

    case 'postal_code': {
      const trimmed = normalizeArabicDigits(values.postal_code).trim();
      if (!trimmed) return undefined;
      if (!/^\d{5}$/.test(trimmed)) {
        return 'الرقم البريدي لازم يكون 5 أرقام';
      }
      return undefined;
    }

    case 'phone': {
      const trimmed = values.phone.trim();
      if (!trimmed) return 'اكتب رقم موبايلك';
      if (!validatePhone(trimmed)) {
        return 'رقم الموبايل مش مظبوط. اكتبه كده: 01xxxxxxxxx';
      }
      return undefined;
    }

    case 'backup_phone': {
      const trimmed = values.backup_phone.trim();
      if (!trimmed) return 'اكتب رقم موبايل احتياطي';
      if (!validatePhone(trimmed)) {
        return 'الرقم الاحتياطي مش مظبوط. اكتبه كده: 01xxxxxxxxx';
      }
      if (
        values.phone.trim() &&
        normalizeEgyptianPhone(trimmed) === normalizeEgyptianPhone(values.phone)
      ) {
        return 'الرقم الاحتياطي لازم يكون مختلف عن رقمك';
      }
      return undefined;
    }

    case 'backup_phone_relation': {
      const trimmed = values.backup_phone_relation.trim();
      if (!trimmed) return 'اختار الرقم الاحتياطي ده لمين';
      if (!VALID_RELATIONS.has(trimmed)) return 'اختار صلة القرابة من القايمة';
      return undefined;
    }
  }
}

export function validateAcceptanceForm(
  values: AcceptanceFormValues
): AcceptanceFieldErrors {
  const errors: AcceptanceFieldErrors = {};
  for (const field of ORDERED_ACCEPTANCE_FIELDS) {
    const err = validateAcceptanceField(field, values);
    if (err) errors[field] = err;
  }
  return errors;
}

export function buildAcceptancePayload(
  values: AcceptanceFormValues
): ScholarshipAcceptancePayload {
  const city = (
    values.cityChoice === OTHER_CITY_OPTION ? values.customCity : values.cityChoice
  ).trim();
  const apartmentNormalized = normalizeArabicDigits(values.apartment).trim();
  const postalNormalized = normalizeArabicDigits(values.postal_code).trim();

  return {
    recipient_name: values.recipient_name.trim(),
    governorate: values.governorate.trim(),
    city,
    area: values.area.trim(),
    street: values.street.trim(),
    building: normalizeArabicDigits(values.building).trim(),
    floor: values.floor.trim(),
    apartment:
      values.floor === 'house' ? null : apartmentNormalized.length > 0 ? apartmentNormalized : null,
    landmark: values.landmark.trim(),
    postal_code: postalNormalized.length > 0 ? postalNormalized : null,
    phone: normalizeEgyptianPhone(values.phone),
    backup_phone: normalizeEgyptianPhone(values.backup_phone),
    backup_phone_relation: values.backup_phone_relation as BackupPhoneRelation,
    social_share_consent: Boolean(values.social_share_consent),
  };
}

export function formatAddressSummary(values: AcceptanceFormValues): string {
  const govLabel = GOVERNORATE_LABELS[values.governorate] ?? values.governorate;
  const city = (
    values.cityChoice === OTHER_CITY_OPTION ? values.customCity : values.cityChoice
  ).trim();
  const floorLabel = FLOOR_LABELS[values.floor] ?? values.floor;
  const buildingText = normalizeArabicDigits(values.building).trim();
  const apartmentText =
    values.floor !== 'house' && values.apartment.trim()
      ? `شقة ${normalizeArabicDigits(values.apartment).trim()}`
      : '';

  const parts = [
    govLabel,
    city,
    values.area.trim(),
    values.street.trim(),
    buildingText ? `عمارة ${buildingText}` : '',
    floorLabel,
    apartmentText,
  ].filter(Boolean);

  return parts.join(' — ');
}
